/**
 * EmailIngestSettings Component
 * Displays and manages the expense email ingest address.
 * Shows create button if no address exists, otherwise shows address, toggle, and default property.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  listProperties,
  expenseEmailApi,
  type PropertyWithStats,
} from '../../lib/api';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import type { EmailIngestAddress } from '../../types/expense.types';

// -------------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------------

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  id: string;
  'aria-label': string;
  'data-testid': string;
}

function ToggleSwitch({ checked, onChange, disabled = false, id, ...props }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={props['aria-label']}
      data-testid={props['data-testid']}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex w-11 h-6 rounded-full border-0 p-0 shrink-0 transition-colors duration-200',
        checked ? 'bg-primary' : 'bg-muted-foreground',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      ].join(' ')}
    >
      <span
        className={[
          'absolute top-0.5 w-5 h-5 bg-background rounded-full transition-[left] duration-200',
          checked ? 'left-[22px]' : 'left-0.5',
        ].join(' ')}
      />
    </button>
  );
}

// -------------------------------------------------------------------------
// Main Component
// -------------------------------------------------------------------------

export function EmailIngestSettings() {
  const [address, setAddress] = useState<EmailIngestAddress | null>(null);
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const [defaultPropertyId, setDefaultPropertyId] = useState<string>('');
  const [isSavingProperty, setIsSavingProperty] = useState(false);
  const [savePropertyError, setSavePropertyError] = useState<string | null>(null);

  const [copied, setCopied] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [emailResult, propertiesResult] = await Promise.all([
        expenseEmailApi.get(),
        listProperties(),
      ]);
      setAddress(emailResult.address);
      setDefaultPropertyId(emailResult.address?.defaultPropertyId ?? '');
      setProperties(propertiesResult.properties);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const result = await expenseEmailApi.create(
        defaultPropertyId ? { defaultPropertyId } : {}
      );
      setAddress(result);
      setDefaultPropertyId(result.defaultPropertyId ?? '');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create forwarding address');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleActive = async (newValue: boolean) => {
    setIsTogglingActive(true);
    setToggleError(null);
    try {
      const result = await expenseEmailApi.update({ isActive: newValue });
      setAddress(result);
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setIsTogglingActive(false);
    }
  };

  const handleSaveDefaultProperty = async () => {
    setIsSavingProperty(true);
    setSavePropertyError(null);
    try {
      const result = await expenseEmailApi.update({
        defaultPropertyId: defaultPropertyId || null,
      });
      setAddress(result);
      setDefaultPropertyId(result.defaultPropertyId ?? '');
    } catch (err) {
      setSavePropertyError(err instanceof Error ? err.message : 'Failed to save default property');
    } finally {
      setIsSavingProperty(false);
    }
  };

  const handleCopy = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address.emailAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for environments without clipboard API
    }
  };

  if (loading) {
    return (
      <div data-testid="email-ingest-settings" className="p-4">
        <div className="space-y-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="email-ingest-settings" className="bg-card rounded-lg p-4 border border-border">
      <h2 className="m-0 mb-4 text-base font-semibold text-foreground">
        Expense Email Forwarding
      </h2>

      {error && (
        <div
          role="alert"
          className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm flex items-center gap-3"
        >
          <span className="flex-1">{error}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadData}
            className="underline min-h-[44px]"
          >
            Retry
          </Button>
        </div>
      )}

      {!address ? (
        /* No address state */
        <div>
          <p className="m-0 mb-4 text-sm text-muted-foreground leading-relaxed">
            Enable expense email forwarding to automatically create expense entries from forwarded receipts and invoices.
          </p>

          {createError && (
            <div
              role="alert"
              className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
            >
              {createError}
            </div>
          )}

          <Button
            data-testid="email-ingest-create-button"
            onClick={handleCreate}
            disabled={isCreating}
            aria-label="Create expense forwarding email address"
          >
            {isCreating ? 'Creating...' : 'Create Forwarding Address'}
          </Button>
        </div>
      ) : (
        /* Address exists state */
        <div>
          {/* Email address display */}
          <div className="flex flex-col gap-1 mb-4">
            <label
              htmlFor="email-ingest-address-display"
              className="block text-xs font-medium text-foreground"
            >
              Forwarding Address
            </label>
            <div className="flex gap-2 items-center">
              <code
                id="email-ingest-address-display"
                data-testid="email-ingest-address"
                className="flex-1 px-3 py-2 bg-accent/50 border border-border rounded-md text-sm font-mono text-foreground select-all break-all"
              >
                {address.emailAddress}
              </code>
              <Button
                variant="outline"
                size="sm"
                data-testid="email-ingest-copy"
                onClick={handleCopy}
                className="shrink-0 min-h-[44px]"
                aria-label={`Copy email address: ${address.emailAddress}`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <p className="m-0 text-[13px] text-muted-foreground">
              Forward receipts and invoices to this address. The system will automatically create expense entries.
            </p>
          </div>

          {/* Active toggle */}
          <div className="flex flex-row items-center justify-between gap-4 mb-4">
            <div>
              <label
                htmlFor="email-ingest-active-toggle"
                className="block text-xs font-medium text-foreground cursor-pointer"
              >
                Email Forwarding Active
              </label>
              <p className="m-0 mt-0.5 text-[13px] text-muted-foreground">
                {address.isActive
                  ? 'Forwarding is enabled — emails will create expense entries.'
                  : 'Forwarding is disabled — emails will be ignored.'}
              </p>
            </div>
            <ToggleSwitch
              id="email-ingest-active-toggle"
              checked={address.isActive}
              onChange={handleToggleActive}
              disabled={isTogglingActive}
              aria-label={address.isActive ? 'Deactivate email forwarding' : 'Activate email forwarding'}
              data-testid="email-ingest-active-toggle"
            />
          </div>

          {toggleError && (
            <div
              role="alert"
              className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
            >
              {toggleError}
            </div>
          )}

          {/* Default property */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="email-ingest-default-property"
              className="block text-xs font-medium text-foreground"
            >
              Default Property
            </label>
            <div className="flex gap-2 items-center">
              <select
                id="email-ingest-default-property"
                data-testid="email-ingest-default-property"
                value={defaultPropertyId}
                onChange={(e) => setDefaultPropertyId(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-input rounded-md bg-card outline-none min-h-[44px]"
                aria-label="Default property for inbound expenses"
              >
                <option value="">-- No default property --</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveDefaultProperty}
                disabled={isSavingProperty || defaultPropertyId === (address.defaultPropertyId ?? '')}
                className="shrink-0 min-h-[44px]"
                aria-label="Save default property"
              >
                {isSavingProperty ? 'Saving...' : 'Save'}
              </Button>
            </div>
            <p className="m-0 text-[13px] text-muted-foreground">
              Expenses without a detected property will be assigned here.
            </p>

            {savePropertyError && (
              <p
                role="alert"
                className="m-0 text-[13px] text-destructive"
              >
                {savePropertyError}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailIngestSettings;
